import { useRef, useState } from "react";
import moment from "moment";

export const useCashBookList = (initialCashTxns = [], ITEMS_PER_PAGE) => {
  const isFirstRender = useRef(true);
  const firstRender = isFirstRender.current;
  const hasCalculatedBalances = useRef(false);

  const initialData =
    firstRender && initialCashTxns?.length
      ? initialCashTxns.slice(0, ITEMS_PER_PAGE)
      : [];

  const [state, setState] = useState({
    isLoading: true,
    cashTxnList: initialData,
    totalMoneyIn: 0,
    totalMoneyOut: 0,
    totalCashTxnFilterCount: 0,
    balanceMap: {},
  });

  const getDateRange = (date) => ({
    start: date?.startDate ? moment(date.startDate).startOf("day").valueOf() : undefined,
    end: date?.endDate ? moment(date.endDate).endOf("day").valueOf() : undefined,
  });

  const calculateTotals = (data) => {
    let totalMoneyIn = 0;
    let totalMoneyOut = 0;

    data.forEach((item) => {
      if (item.type === "in") totalMoneyIn += item.amount || 0;
      else totalMoneyOut += item.amount || 0;
    });

    return { totalFilteredCount: data?.length || 0, totalMoneyIn, totalMoneyOut };
  };

  const computeRunningBalances = (data) => {
    const sorted = [...data].sort((a, b) => (a.transacted_at || 0) - (b.transacted_at || 0));
    const map = {};
    let running = 0;
    sorted.forEach((txn) => {
      running += txn.type === "in" ? (txn.amount || 0) : -(txn.amount || 0);
      map[txn.id] = running;
    });
    return map;
  };

  const VIEW_TYPE_MAP = { "Cash In": "in", "Cash Out": "out" };

  const handleCashTxn = async ({ business, filters, limit, offset, isCalculateTotal = true, forceRecalculateBalances = false }) => {
    const { date, amount, view, sort, keywords, category } = filters || {};
    const viewType = VIEW_TYPE_MAP[view] || view;

    setState((prev) => ({ ...prev, isLoading: true }));

    const { start, end } = getDateRange(date);
    if (forceRecalculateBalances) hasCalculatedBalances.current = false;

    try {
      let totals = {};

      let newBalanceMap = null;

      if (isCalculateTotal) {
        let rawData;
        if (firstRender) {
          rawData = initialCashTxns;
        } else {
          rawData = await business
            .getCashTxn(start, end, keywords || "", viewType, undefined, undefined, undefined, amount, category)
            .unsafeFetchRaw();
        }
        totals = calculateTotals(rawData);
      }

      // Compute running balance only once — always from ALL transactions, never filtered
      if (!hasCalculatedBalances.current) {
        const allRaw = firstRender
          ? initialCashTxns
          : await business.getCashTxn(undefined, undefined, "", undefined, undefined, undefined, undefined, undefined, undefined).unsafeFetchRaw();
        newBalanceMap = computeRunningBalances(allRaw);
        hasCalculatedBalances.current = true;
      }

      const paginatedItems = firstRender
        ? initialData
        : await business
            .getCashTxn(start, end, keywords || "", viewType, limit, offset, sort || "Newest", amount, category)
            .fetch();

      const updatedList = offset > 0 ? [...state.cashTxnList, ...paginatedItems] : paginatedItems;

      isFirstRender.current = false;

      setState((prev) => ({
        ...prev,
        isLoading: false,
        cashTxnList: updatedList,
        ...(isCalculateTotal && {
          totalMoneyIn: totals.totalMoneyIn,
          totalMoneyOut: totals.totalMoneyOut,
          totalCashTxnFilterCount: totals.totalFilteredCount,
        }),
        ...(newBalanceMap && { balanceMap: newBalanceMap }),
      }));
    } catch (error) {
      console.error(error);
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  return { ...state, handleCashTxn };
};
